import { apiClient, ApiError } from '../client';
import { API_ENDPOINTS } from '../config';
import { getOrganizationId } from '../organization';
import { getAiAnalyticsUrl } from '../aiAnalyticsUrl';

export interface Template {
  id: string;
  name: string;
  category: string;
  language: string;
  status: 'approved' | 'pending' | 'rejected' | 'draft';
  business_account_id: string;
  whatsapp_template_id?: string;
  meta_response?: Record<string, any>;
  parent_template_id?: string;
  version: string;
  created_by?: string;
  updated_by?: string;
  submitted_at?: string;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  // Backend sends camelCase versions
  createdAt?: string;
  updatedAt?: string;
  businessAccountId?: string;
  whatsappTemplateId?: string;
  metaResponse?: Record<string, any>;
  parentTemplateId?: string;
  createdBy?: string;
  updatedBy?: string;
  submittedAt?: string;
  deletedAt?: string;
}

export interface CreateTemplateData {
  businessAccountId: string;
  name: string;
  category: string;
  language: string;
  components: any[];
  userId?: string;
  templateType?: 'TEXT_ONLY' | 'TEXT_WITH_BUTTONS' | 'IMAGE_HEADER' | 'IMAGE_WITH_BUTTONS' | 'VIDEO_HEADER' | 'VIDEO_WITH_BUTTONS' | 'DOCUMENT_HEADER' | 'OTP_TEMPLATE' | 'OTP_WITH_BUTTONS' | 'CAROUSEL';
  mediaFiles?: Array<{
    url: string;
    type: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    componentIndex: number;
  }>;
}

export interface SendTemplateData {
  businessAccountId: string;
  to: string;
  templateName: string;
  language: string;
  parameters?: string[];
  /**
   * The conversation this template belongs to. REQUIRED for inbox sends —
   * without it the backend stores the message with a null conversation_id
   * (orphaned from the thread, never returned by the messages query) and skips
   * the real-time socket emit. Optional in the type only for non-inbox callers.
   */
  conversationId?: string | null;
  /** Contact this template is sent to (used for limit checks + linking). */
  contactId?: string | null;
}

export interface ImportTemplatesData {
  businessAccountId: string;
}

export interface SyncTemplatesData {
  businessAccountId: string;
}

export const templatesApi = {
  normalizeGeneratedTemplateText(text: string) {
    // Meta templates expect variables in {{1}} or {{name}} format (double braces, no spaces).
    // Some AI responses may include Handlebars-style triple braces (e.g., {{{1}}}).
    return text
      .replace(/\{\{\{/g, '{{')
      .replace(/\}\}\}/g, '}}')
      .replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, inner) => `{{${String(inner).trim()}}}`);
  },

  /**
   * Get all templates
   */
  async getAll(businessAccountId?: string) {
    const params = businessAccountId ? { businessAccountId: businessAccountId } : {};
    return apiClient.get(API_ENDPOINTS.TEMPLATES, { params });
  },

  /**
   * Get template types
   */
  async getTypes() {
    return apiClient.get('/templates/types');
  },

  /**
   * Get template by ID
   */
  async getById(id: string) {
    return apiClient.get(`${API_ENDPOINTS.TEMPLATES}/${id}`);
  },

  /**
   * Create template
   */
  async create(data: CreateTemplateData) {
    return apiClient.post(API_ENDPOINTS.TEMPLATES, data);
  },

  /**
   * Delete template
   */
  async delete(id: string) {
    return apiClient.delete(`${API_ENDPOINTS.TEMPLATES}/${id}`);
  },

  /**
   * Send template message
   */
  async sendTemplate(data: SendTemplateData) {
    return apiClient.post('/whatsapp/send-template', data);
  },

  /**
   * Import templates
   */
  async importTemplates(data: ImportTemplatesData) {
    return apiClient.post('/whatsapp/import-templates', data);
  },

  /**
   * Sync templates
   */
  async syncTemplates(data: SyncTemplatesData) {
    return apiClient.post('/templates/sync', data);
  },

  /**
   * Preview template
   */
  async previewTemplate(id: string, variables?: Record<string, string>) {
    return apiClient.post(`/templates/${id}/preview`, { variables });
  },

  /**
   * Generate AI templates
   */
  async generateAITemplates(data: {
    category: string;
    specifications: string;
    language: string;
  }) {
    const organizationId = getOrganizationId();
    const token = localStorage.getItem('auth_token');

    // First, get AI-generated prompt from generate-prompt endpoint
    let specifications = data.specifications;
    if (organizationId) {
      try {
        const promptRes = await fetch(getAiAnalyticsUrl('/start-flow/generate-prompt'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({ organization_id: organizationId, type: 'template' }),
        });
        if (promptRes.ok) {
          const promptData = await promptRes.json();
          if (promptData?.prompt) {
            specifications = promptData.prompt;
          }
        }
      } catch {
        // fall through and use original specifications
      }
    }

    // Use FormData for this endpoint as it expects form-data
    const formData = new FormData();
    formData.append('specifications', specifications);
    formData.append('language', data.language);
    // Only forward a category the backend accepts (AUTHENTICATION | MARKETING |
    // UTILITY). Anything else (e.g. admin categories like "General") is omitted so
    // the backend infers it, avoiding a 400 "Category must be…" error.
    if (['AUTHENTICATION', 'MARKETING', 'UTILITY'].includes(data.category)) {
      formData.append('category', data.category);
    }
    if (organizationId) {
      formData.append('organization_id', organizationId);
    }

    const response = await fetch(getAiAnalyticsUrl('/generate-templates'), {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // FastAPI surfaces the human-readable reason in `detail` (a string for
      // HTTPException). Map it into `error` so ApiError carries the real message
      // instead of falling back to the bare "HTTP <status>" string.
      const detailMessage =
        typeof errorData?.detail === 'string'
          ? errorData.detail
          : (errorData?.error ?? errorData?.message);
      throw ApiError.fromResult(
        { ...errorData, error: detailMessage, statusCode: response.status },
        `HTTP ${response.status}`,
      );
    }

    const json = await response.json();

    if (json?.templates && Array.isArray(json.templates)) {
      json.templates = json.templates.map((template: any) => {
        if (!template?.components || !Array.isArray(template.components)) return template;

        return {
          ...template,
          components: template.components.map((component: any) => {
            if (typeof component?.text !== 'string') return component;

            return {
              ...component,
              text: templatesApi.normalizeGeneratedTemplateText(component.text),
            };
          }),
        };
      });
    }

    return json;
  },
};
